import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { useAuthContext } from 'src/auth/hooks';
import { ROLES } from 'src/constants';
import { API_ENDPOINTS, fetcher } from 'src/utils/axios';

// ----------------------------------------------------------------------

/**
 * What the signed-in user can do in Off Campus. A coordinator (or super admin)
 * can do everything; the secretary sees everything and does what the
 * coordinators switched on in Off Campus settings (secretary-access.js);
 * anyone else, a counsellor, only reads. The server checks the same, so this
 * only decides which buttons show.
 *
 * isCoordinator: show coordinator-only work.
 * canSeeAll: the full Off Campus pages (coordinator or secretary), not the
 *   counsellor's single read-only placement.
 * can(permission): one of the secretary permissions.
 */
export default function useOffCampusAccess() {
  const { user } = useAuthContext();
  const roles = useMemo(() => (Array.isArray(user?.roles) ? user.roles : []), [user]);
  const isCoordinator = roles.includes(ROLES.USER_ROLE_OFF_CAMPUS_COORDINATOR) || roles.includes(ROLES.SUPER_ADMIN);
  const isSecretary = !isCoordinator && roles.includes(ROLES.USER_ROLE_OFF_CAMPUS_SECRETARY);

  // Only the secretary's permissions need the server; a coordinator has them all
  const { data, isLoading } = useSWR(isSecretary ? API_ENDPOINTS.offCampus.access : null, fetcher);
  const permissions = useMemo(() => data?.permissions || [], [data]);

  const can = useCallback((permission) => isCoordinator || permissions.includes(permission), [isCoordinator, permissions]);

  return useMemo(
    () => ({
      isCoordinator,
      isSecretary,
      canSeeAll: isCoordinator || isSecretary,
      can,
      loading: isSecretary && isLoading,
    }),
    [isCoordinator, isSecretary, can, isLoading]
  );
}
